/**
 * Monthly ledger helpers - the math that powers the Project/Product pages
 * and the Reports scorecard.
 *
 * Mental model:
 *   - A ledger is `Record<"YYYY-MM", MonthlyLedgerEntry>`.
 *   - `base` is auto-seeded when a project completes: `expectedValue / 12`
 *     spread from the go-live month through December, plus full forecast years.
 *   - `adjustments` is the manual layer on top (+/-).
 *   - Month total = base + adjustments.
 */

import type { MonthlyLedgerEntry } from '../types'

export const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const

/** "YYYY-MM" string for year/month (1-12). */
export const monthKey = (year: number, month: number) =>
  `${year}-${String(month).padStart(2, '0')}`

/** Safely read a ledger entry, returning zeros if the slot is missing. */
export function getEntry(
  ledger: Record<string, MonthlyLedgerEntry> | undefined,
  key: string,
): MonthlyLedgerEntry {
  const e = ledger?.[key]
  return {
    base: e?.base ?? 0,
    adjustments: e?.adjustments ?? 0,
    adjustmentNotes: e?.adjustmentNotes ?? [],
  }
}

/** base + adjustments for a month (missing month → 0). */
export const monthTotal = (
  ledger: Record<string, MonthlyLedgerEntry> | undefined,
  key: string,
): number => {
  const e = getEntry(ledger, key)
  return e.base + e.adjustments
}

/** Sum of month totals for a given year (all 12 months). */
export function yearTotal(
  ledger: Record<string, MonthlyLedgerEntry> | undefined,
  year: number,
): number {
  let total = 0
  for (let m = 1; m <= 12; m++) total += monthTotal(ledger, monthKey(year, m))
  return total
}

/**
 * Sum of month totals from January through (and including) the given month
 * of the given year. Used for "YTD" columns on the scorecard.
 */
export function yearToDateTotal(
  ledger: Record<string, MonthlyLedgerEntry> | undefined,
  year: number,
  throughMonth: number,
): number {
  let total = 0
  for (let m = 1; m <= throughMonth; m++) total += monthTotal(ledger, monthKey(year, m))
  return total
}

/**
 * Sum of month totals from (but not including) `throughMonth` through
 * December of the given year. Used for "remaining target" columns.
 */
export function yearRemainingTotal(
  ledger: Record<string, MonthlyLedgerEntry> | undefined,
  year: number,
  throughMonth: number,
): number {
  let total = 0
  for (let m = throughMonth + 1; m <= 12; m++) total += monthTotal(ledger, monthKey(year, m))
  return total
}

/**
 * Pick the years a ledger has any data in, sorted ascending.
 * Always includes the current year so owners can add adjustments
 * to an otherwise-empty year.
 */
export function ledgerYears(
  ledger: Record<string, MonthlyLedgerEntry> | undefined,
): number[] {
  const years = new Set<number>()
  years.add(new Date().getFullYear())
  for (const key of Object.keys(ledger || {})) {
    const y = Number(key.slice(0, 4))
    if (!Number.isNaN(y)) years.add(y)
  }
  return Array.from(years).sort((a, b) => a - b)
}

/**
 * Merge a batch of TOTAL edits back into a ledger, returning the new
 * ledger object. Each edit is the owner's desired total for that month;
 * we keep `base` untouched and derive `adjustments = total - base` so
 * future auto-seed writes from new projects still stack correctly.
 *
 * Month keys that don't exist yet are created with base=0 so owner
 * overrides on empty months still persist.
 *
 * Rounding: totals are rounded to the nearest integer before computing
 * the adjustment, because the UI only shows whole numbers and we don't
 * want "edit with no visible change" to produce micro-deltas like 0.33.
 */
export function applyTotalEdits(
  ledger: Record<string, MonthlyLedgerEntry> | undefined,
  edits: Record<string, number>,
  note: string,
): Record<string, MonthlyLedgerEntry> {
  const next: Record<string, MonthlyLedgerEntry> = { ...(ledger || {}) }
  const trimmedNote = note.trim()
  for (const [key, rawTotal] of Object.entries(edits)) {
    const existing = getEntry(ledger, key)
    const newTotal = Math.round(rawTotal)
    const prevTotal = Math.round(existing.base + existing.adjustments)
    if (newTotal === prevTotal) continue
    const notes = [...existing.adjustmentNotes]
    if (trimmedNote) notes.push(trimmedNote)
    next[key] = {
      base: existing.base,
      adjustments: newTotal - existing.base,
      adjustmentNotes: notes,
    }
  }
  return next
}

/**
 * Diff a proposed `edits` set (keyed month → desired total) against the
 * current ledger. Returns only the months that actually changed (at whole-
 * number resolution), with before/after/delta for the confirm modal.
 */
export interface LedgerDiffRow {
  key: string
  monthLabel: string
  base: number
  before: number
  after: number
  delta: number
}

export function diffEdits(
  ledger: Record<string, MonthlyLedgerEntry> | undefined,
  edits: Record<string, number>,
): LedgerDiffRow[] {
  const rows: LedgerDiffRow[] = []
  for (const [key, rawTotal] of Object.entries(edits)) {
    const existing = getEntry(ledger, key)
    const newTotal = Math.round(rawTotal)
    const prevTotal = Math.round(existing.base + existing.adjustments)
    if (newTotal === prevTotal) continue
    const monthIdx = Number(key.slice(5, 7)) - 1
    const year = key.slice(0, 4)
    const monthLabel = `${MONTH_NAMES[monthIdx] ?? key} ${year}`
    rows.push({
      key,
      monthLabel,
      base: existing.base,
      before: prevTotal,
      after: newTotal,
      delta: newTotal - prevTotal,
    })
  }
  return rows.sort((a, b) => a.key.localeCompare(b.key))
}
